/**
 * Waitlist Join page (epic7 A5) — public, unauthenticated waitlist sign-up.
 *
 *   SPA route: #/waitlist?service=:serviceId[&serviceName=...&resource=:id]
 *   Endpoint:  POST /v1/book/:tenantSlug/waitlist  (publicWaitlistRoutes)
 *     body → { service_id, resource_id?, name, email, phone_e164,
 *              preferred_date_from?, preferred_date_to? }
 *     201  → { waitlist_entry_id, position, service_name, short_ref }
 *
 * Client validation MIRRORS the server Zod (waitlistJoinSchema):
 *   name 1..120 · email valid/≤320 · phone E.164 (+ then 7..15 digits).
 *
 * GDPR (Article 7): this form collects personal data, so a REQUIRED terms
 * checkbox gates submission (mirrors the consent posture used at checkout).
 * SMS/marketing opt-ins are optional and default OFF (no pre-ticking).
 *
 * Money is DISPLAY-only: a "no payment until a slot is confirmed" notice — this
 * page never charges. CSP/nonce-safe: no inline handlers, all addEventListener.
 *
 * Render contract: renderWaitlistJoin() -> void (writes into #app; reads hash).
 */

import { normalizeE164 } from '../../../../shared/frontend/js/phone.js';

// ── Server-mirrored validation ──────────────────────────────────────────

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const E164_RE = /^\+[1-9]\d{6,14}$/;
const NAME_MAX = 120;
const EMAIL_MAX = 320;

interface WaitlistResult {
  waitlist_entry_id: string;
  position: number;
  service_name: string;
  short_ref: string | null;
}

interface Rfc7807 {
  detail?: string;
  title?: string;
}

// ── Helpers ─────────────────────────────────────────────────────────────

function escapeHtml(str: string): string {
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
}

function parseHashQuery(): URLSearchParams {
  const hash = window.location.hash;
  const qIndex = hash.indexOf('?');
  return new URLSearchParams(qIndex >= 0 ? hash.slice(qIndex + 1) : '');
}

function resolveSlug(): string {
  const cfg = (window as { __TIMESLOT_CONFIG__?: { tenantSlug?: string } }).__TIMESLOT_CONFIG__;
  if (cfg?.tenantSlug) return cfg.tenantSlug;
  const match = window.location.pathname.match(/^\/book\/([a-z0-9-]+)/);
  return match?.[1] ?? '';
}

function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] ?? s[v] ?? s[0]!);
}

function setFieldError(id: string, message: string): void {
  const el = document.getElementById(id);
  if (el) el.textContent = message;
}

function clearFieldErrors(form: HTMLFormElement): void {
  for (const el of form.querySelectorAll('.ts-form__error')) el.textContent = '';
}

// ── Render: form ────────────────────────────────────────────────────────

export function renderWaitlistJoin(): void {
  const appEl = document.getElementById('app');
  if (!appEl) return;

  const q = parseHashQuery();
  const serviceId = q.get('service') ?? q.get('serviceId') ?? '';
  const serviceName = q.get('serviceName') ?? '';
  const resourceId = q.get('resource') ?? '';

  if (!serviceId) {
    appEl.innerHTML = `
      <div class="ts-page ts-page--waitlist">
        <div class="ts-empty-state" role="alert">
          <p class="ts-empty-state__title">No service selected</p>
          <p class="ts-empty-state__message">Pick a service first, then join its waitlist.</p>
          <button type="button" class="ts-btn ts-btn--primary" data-action="back">Browse services</button>
        </div>
      </div>`;
    appEl.querySelector('[data-action="back"]')?.addEventListener('click', () => {
      window.location.hash = '#/services';
    });
    return;
  }

  const serviceLine = serviceName
    ? `<p class="ts-page__subtitle">Waitlist for <strong>${escapeHtml(serviceName)}</strong></p>`
    : '<p class="ts-page__subtitle">Join the waitlist and we will let you know when a slot opens.</p>';

  appEl.innerHTML = `
    <div class="ts-page ts-page--waitlist">
      <header class="ts-page__header">
        <button type="button" class="ts-back-btn" data-action="back" aria-label="Back to services">
          <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
            <path d="M12 4l-6 6 6 6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          Back
        </button>
        <h1 class="ts-page__title">Join the waitlist</h1>
        ${serviceLine}
      </header>

      <div class="ts-alert ts-alert--info" role="note">
        <div class="ts-alert__content">No payment is taken until a slot is confirmed for you.</div>
      </div>

      <form id="waitlist-form" class="ts-form" novalidate>
        <input type="hidden" name="service_id" value="${escapeHtml(serviceId)}">
        ${resourceId ? `<input type="hidden" name="resource_id" value="${escapeHtml(resourceId)}">` : ''}

        <div class="ts-form__group">
          <label for="wl-name" class="ts-form__label">
            Full name <span class="ts-form__required" aria-label="required">*</span>
          </label>
          <input type="text" id="wl-name" name="name" class="ts-form__input" required
            maxlength="${NAME_MAX}" autocomplete="name" aria-describedby="wl-name-error">
          <span id="wl-name-error" class="ts-form__error" role="alert" aria-live="polite"></span>
        </div>

        <div class="ts-form__group">
          <label for="wl-email" class="ts-form__label">
            Email <span class="ts-form__required" aria-label="required">*</span>
          </label>
          <input type="email" id="wl-email" name="email" class="ts-form__input" required
            maxlength="${EMAIL_MAX}" autocomplete="email" placeholder="you@example.ie"
            aria-describedby="wl-email-error">
          <span id="wl-email-error" class="ts-form__error" role="alert" aria-live="polite"></span>
        </div>

        <div class="ts-form__group">
          <label for="wl-phone" class="ts-form__label">
            Mobile number <span class="ts-form__required" aria-label="required">*</span>
          </label>
          <input type="tel" id="wl-phone" name="phone_e164" class="ts-form__input" required
            autocomplete="tel" inputmode="tel" placeholder="+353871234567"
            aria-describedby="wl-phone-hint wl-phone-error">
          <span id="wl-phone-hint" class="ts-form__hint">Include the country code, e.g. +353 for Ireland.</span>
          <span id="wl-phone-error" class="ts-form__error" role="alert" aria-live="polite"></span>
        </div>

        <div class="ts-form__group ts-form__group--checkbox">
          <label class="ts-form__checkbox-label" for="wl-terms">
            <input type="checkbox" id="wl-terms" name="terms_accepted" class="ts-form__checkbox"
              required aria-describedby="wl-terms-error">
            <span>I agree to the <a href="/legal?tab=terms" target="_blank" rel="noopener">Terms of Service</a>
              and <a href="/legal?tab=privacy" target="_blank" rel="noopener">Privacy Policy</a>
              <span class="ts-form__required" aria-label="required">*</span></span>
          </label>
          <span id="wl-terms-error" class="ts-form__error" role="alert" aria-live="polite"></span>
        </div>

        <div id="wl-global-error" class="ts-form__global-error" role="alert" aria-live="polite" hidden></div>

        <button type="submit" id="wl-submit" class="ts-btn ts-btn--primary ts-btn--large ts-btn--full-width">
          Join waitlist
        </button>
      </form>

      <div id="wl-result"></div>
    </div>`;

  appEl.querySelector('[data-action="back"]')?.addEventListener('click', () => {
    window.location.hash = serviceId ? `#/book/${serviceId}` : '#/services';
  });

  const form = document.getElementById('waitlist-form') as HTMLFormElement | null;
  form?.addEventListener('submit', (e) => {
    e.preventDefault();
    void handleSubmit(form);
  });
}

// ── Submit ──────────────────────────────────────────────────────────────

function validate(form: HTMLFormElement): {
  ok: boolean;
  values?: { name: string; email: string; phone: string };
} {
  clearFieldErrors(form);
  const fd = new FormData(form);
  const name = ((fd.get('name') as string) ?? '').trim();
  const email = ((fd.get('email') as string) ?? '').trim();
  const phoneRaw = ((fd.get('phone_e164') as string) ?? '').trim();
  const terms = fd.get('terms_accepted') === 'on';

  let ok = true;
  // Normalise a free-text entry ("087 123 4567", "+353 87 123 4567") to E.164 so the
  // strict server schema accepts it; unparseable input shows an inline error (not a
  // silent drop). Mirrors booking-form's normalize-before-validate.
  let phone = phoneRaw;
  if (name.length === 0) {
    setFieldError('wl-name-error', 'Name is required');
    ok = false;
  } else if (name.length > NAME_MAX) {
    setFieldError('wl-name-error', `Name must be ${NAME_MAX} characters or fewer`);
    ok = false;
  }
  if (email.length === 0) {
    setFieldError('wl-email-error', 'Email is required');
    ok = false;
  } else if (!EMAIL_RE.test(email) || email.length > EMAIL_MAX) {
    setFieldError('wl-email-error', 'Enter a valid email address');
    ok = false;
  }
  if (phoneRaw.length === 0) {
    setFieldError('wl-phone-error', 'Mobile number is required');
    ok = false;
  } else {
    const normalized = normalizeE164(phoneRaw);
    if (!normalized || !E164_RE.test(normalized)) {
      setFieldError(
        'wl-phone-error',
        'Enter a valid mobile number, e.g. 087 123 4567 or +353 87 123 4567',
      );
      ok = false;
    } else {
      phone = normalized;
    }
  }
  if (!terms) {
    setFieldError('wl-terms-error', 'You must accept the terms to continue');
    ok = false;
  }

  return ok ? { ok, values: { name, email, phone } } : { ok };
}

async function handleSubmit(form: HTMLFormElement): Promise<void> {
  const globalError = document.getElementById('wl-global-error');
  if (globalError) {
    globalError.hidden = true;
    globalError.textContent = '';
  }

  const result = validate(form);
  if (!result.ok || !result.values) {
    // Move focus to the first invalid field for screen-reader + keyboard users.
    form
      .querySelector<HTMLElement>('.ts-form__error:not(:empty)')
      ?.closest('.ts-form__group')
      ?.querySelector<HTMLElement>('input')
      ?.focus();
    return;
  }

  const slug = resolveSlug();
  const fd = new FormData(form);
  const serviceId = (fd.get('service_id') as string) ?? '';
  const resourceId = (fd.get('resource_id') as string) ?? '';

  const submitBtn = document.getElementById('wl-submit') as HTMLButtonElement | null;
  if (submitBtn) {
    submitBtn.disabled = true;
    submitBtn.setAttribute('aria-busy', 'true');
    submitBtn.textContent = 'Joining…';
  }

  try {
    const body: Record<string, string | boolean> = {
      service_id: serviceId,
      name: result.values.name,
      email: result.values.email,
      phone_e164: result.values.phone,
      // GDPR Article 7: the required terms checkbox (validated above) is sent so
      // the server can enforce + persist consent (schema: terms_accepted literal true).
      terms_accepted: true,
    };
    if (resourceId) body['resource_id'] = resourceId;

    const response = await fetch(`/v1/book/${encodeURIComponent(slug)}/waitlist`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const err = (await response.json().catch(() => ({}))) as Rfc7807;
      const message =
        response.status === 404
          ? 'That service is no longer available. Please pick another.'
          : response.status === 429
            ? 'Too many attempts. Please wait a little while and try again.'
            : err.detail || err.title || 'We could not add you to the waitlist. Please try again.';
      if (globalError) {
        globalError.hidden = false;
        globalError.textContent = message;
      }
      return;
    }

    const data = (await response.json()) as WaitlistResult;
    renderSuccess(data);
  } catch {
    if (globalError) {
      globalError.hidden = false;
      globalError.textContent = 'Connection error. Please check your internet and try again.';
    }
  } finally {
    if (submitBtn) {
      submitBtn.disabled = false;
      submitBtn.removeAttribute('aria-busy');
      submitBtn.textContent = 'Join waitlist';
    }
  }
}

function renderSuccess(data: WaitlistResult): void {
  const appEl = document.getElementById('app');
  if (!appEl) return;

  const refLine = data.short_ref
    ? `<p class="ts-confirmation__booking-id">Reference: <strong>${escapeHtml(data.short_ref)}</strong></p>`
    : '';

  appEl.innerHTML = `
    <div class="ts-page ts-page--waitlist">
      <div class="ts-confirmation" role="status" aria-live="polite">
        <div class="ts-confirmation__icon ts-confirmation__icon--success" aria-hidden="true">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"/><polyline points="8 12 11 15 16 9"/>
          </svg>
        </div>
        <h1 class="ts-confirmation__title">You're on the waitlist</h1>
        <p class="ts-confirmation__message">
          You are <strong>${escapeHtml(ordinal(data.position))}</strong> in line for
          <strong>${escapeHtml(data.service_name)}</strong>.
        </p>
        ${refLine}
        <p class="ts-confirmation__note">We will email you as soon as a slot opens up. No payment is taken until then.</p>
        <div class="ts-confirmation__actions">
          <button type="button" class="ts-btn ts-btn--secondary ts-btn--full-width" data-action="services">
            Back to services
          </button>
        </div>
      </div>
    </div>`;

  appEl.querySelector('[data-action="services"]')?.addEventListener('click', () => {
    window.location.hash = '#/services';
  });
}
